import { useRef, useState, useEffect, useMemo } from "react";
import { useAuth, API, authFetch } from "@/App";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Zap, Mail, FileSpreadsheet, History, LogOut, Loader2,
  Shield, FileText, Upload, Eraser, Trash2, Building2, Palette,
} from "lucide-react";
import { toast } from "sonner";
import { FabricReviewModal } from "@/components/fabric/FabricReviewModal";
import { extractEmailImageCandidates } from "@/components/fabric/emailImages";
import { ExtractionValidationModal } from "@/components/ExtractionValidationModal";
import { useDashboardState } from "@/components/dashboard/hooks/useDashboardState";
import { useExtraction } from "@/components/dashboard/hooks/useExtraction";
import { usePdfUpload } from "@/components/dashboard/hooks/usePdfUpload";
import { useChat } from "@/components/dashboard/hooks/useChat";
import { useOrderActions } from "@/components/dashboard/hooks/useOrderActions";
import { EnrichModal } from "@/components/dashboard/modals/EnrichModal";
import { ConflictsModal } from "@/components/dashboard/modals/ConflictsModal";

// Sidebar Component
const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [fabUsineCount, setFabUsineCount] = useState(null);

  useEffect(() => {
    const fetchCount = () => {
      authFetch(`${API}/snapshots/fab-usine-trend`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setFabUsineCount(data.current); })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60 * 1000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const navItems = [
    { icon: FileSpreadsheet, label: "Tableau", path: "/tableau" },
    { icon: Mail, label: "Extracteur", path: "/dashboard" },
    { icon: History, label: "Historique", path: "/history" },
    ...(user?.is_admin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  return (
    <aside className="sidebar flex flex-col" data-testid="sidebar">
      <div className="p-6 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-zinc-950 font-['Manrope']">OrderFlow</span>
        </div>
      </div>
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <button
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-colors ${
                  location.pathname === item.path
                    ? "bg-orange-50 text-orange-600"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      {fabUsineCount !== null && (
        <div className="px-4 py-3 border-t border-zinc-200">
          <span className="text-sm text-orange-600 font-medium" data-testid="sidebar-fab-count">
            A fabriquer usine : {fabUsineCount}
          </span>
        </div>
      )}
      <div className="p-4 border-t border-zinc-200">
        <div className="flex items-center gap-3 mb-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 bg-zinc-200 rounded-full flex items-center justify-center">
              <span className="text-zinc-600 font-medium">{user?.name?.charAt(0) || "U"}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button variant="outline" className="w-full justify-start gap-2" onClick={handleLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4" />
          Déconnexion
        </Button>
      </div>
    </aside>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Core shared state
  const state = useDashboardState();
  const {
    emailContent, setEmailContent,
    extractedData, setExtractedData,
    editedData, setEditedData,
    currentOrderId, setCurrentOrderId,
    isExtracting, setIsExtracting,
    isExporting, setIsExporting,
    isEditing, setIsEditing,
    collectedEmails, setCollectedEmails,
    validationModal, setValidationModal,
    isSavingValidation, setIsSavingValidation,
    conflictsModal, setConflictsModal,
    customConflictValue, setCustomConflictValue,
    isEnrichModalOpen, setIsEnrichModalOpen,
    enrichEmailContent, setEnrichEmailContent,
    isEnriching, setIsEnriching,
    companyProfiles, selectedProfileId, setSelectedProfileId,
    detectedCompany, setDetectedCompany,
    extractionInfo, setExtractionInfo,
  } = state;

  // Email images kept for the fabric verification screen (from rich paste or .eml/.msg file)
  const [emailAssets, setEmailAssets] = useState(null);
  const [fabricReview, setFabricReview] = useState(null);
  const emailAssetsRef = useRef(null);
  emailAssetsRef.current = emailAssets;
  useEffect(() => { if (!emailContent.trim()) setEmailAssets(null); }, [emailContent]);
  const emailCandidates = useMemo(
    () => (emailAssets ? extractEmailImageCandidates(emailAssets.html, emailAssets.images) : null),
    [emailAssets]
  );
  const handleEmailPaste = (e) => {
    const html = e.clipboardData?.getData("text/html");
    if (html && /<img|imagedata/i.test(html)) setEmailAssets({ html, images: [] });
  };
  const handleOrderCreated = (orderId, data) => {
    const assets = emailAssetsRef.current;
    if (!assets) return;
    const candidates = extractEmailImageCandidates(assets.html, assets.images);
    if (candidates.items.length) setFabricReview({ orderId, orderName: data?.NAME || data?.name || "", candidates });
    else if (candidates.unavailable) toast.info("Les images de l'email n'ont pas pu être copiées : glissez le fichier .msg / .eml pour les récupérer.");
  };

  // PDF / email file upload
  const { isUploadingPdf, isDraggingPdf, pdfInputRef, handleDragOver, handleDragLeave, handleDrop, handleFileInputChange } =
    usePdfUpload({ setEmailContent, onEmailParsed: (data) => setEmailAssets({ html: data.html || "", images: data.images || [] }) });

  // Chat
  const { resetChat } = useChat({
    currentOrderId, emailContent, extractedData, setExtractedData, setEditedData,
  });

  // Extraction logic
  const { handleExtract, handleValidationSave, handleEnrichFromMainZone, handleEnrich, resolveConflict, applyResolvedConflicts } =
    useExtraction({
      emailContent, setEmailContent, extractedData, setExtractedData, editedData, setEditedData,
      currentOrderId, setCurrentOrderId, selectedProfileId, validationModal, setValidationModal,
      setIsSavingValidation, detectedCompany, setDetectedCompany, collectedEmails, setCollectedEmails,
      setExtractionInfo, setIsExtracting, conflictsModal, setConflictsModal,
      enrichEmailContent, setEnrichEmailContent, setIsEnrichModalOpen, setIsEnriching,
      navigate, onOrderCreated: handleOrderCreated,
    });

  // Order lifecycle actions
  const { handleExport, handleEditToggle, handleSaveEdits, handleDataChange, handleClearEmailZone, handleClearAll } =
    useOrderActions({
      extractedData, setExtractedData, editedData, setEditedData,
      isEditing, setIsEditing, currentOrderId, setCurrentOrderId,
      setIsExporting, emailContent, setEmailContent,
      collectedEmails, setCollectedEmails, detectedCompany,
      setConflictsModal, resetChat, setDetectedCompany,
      navigate,
    });

  return (
    <div className="flex min-h-screen bg-zinc-50">
      <Sidebar />

      <main className="main-content flex-1" data-testid="dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 font-['Manrope']">
            Bonjour, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-zinc-600 mt-1">
            Extrayez et exportez vos commandes en quelques clics
          </p>
        </div>

        {/* Workbench */}
        <div className="workbench-layout">
          {/* Raw Input Panel */}
          <div className="raw-input-panel">
            <div className="panel-header">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-zinc-400" />
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email brut</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Company Profile Selector */}
                {companyProfiles.length > 0 && (
                  <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
                    <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="profile-selector">
                      <Building2 className="w-4 h-4 mr-2 text-zinc-400" />
                      <SelectValue placeholder="Auto-détection" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto" data-testid="profile-option-auto">
                        <span className="flex items-center gap-2">
                          <Zap className="w-3 h-3 text-orange-500" />
                          Auto-détection
                        </span>
                      </SelectItem>
                      {companyProfiles.map((profile) => (
                        <SelectItem key={profile.profile_id} value={profile.profile_id} data-testid={`profile-option-${profile.profile_id}`}>
                          {profile.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  onClick={handleExtract}
                  disabled={isExtracting || !emailContent.trim()}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold gap-2"
                  data-testid="extract-btn"
                >
                  {isExtracting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Extraction...</>
                  ) : (
                    <><Zap className="w-4 h-4" />{extractedData ? "Compléter" : "Extraire"}</>
                  )}
                </Button>
                {emailContent.trim() && (
                  <Button
                    variant="outline"
                    onClick={handleClearEmailZone}
                    className="gap-2 border-zinc-300 text-zinc-600 hover:bg-zinc-100"
                    title="Effacer la zone de texte (conserver les données)"
                    data-testid="clear-zone-btn"
                  >
                    <Eraser className="w-4 h-4" />
                    Nettoyer zone
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleClearAll}
                  className="gap-2 border-red-300 text-red-600 hover:bg-red-50"
                  title="Tout réinitialiser (zone + données extraites)"
                  data-testid="clear-all-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Effacer tout
                </Button>
              </div>
            </div>
            <div className="panel-body p-0">
              {/* PDF Upload Zone */}
              <div
                className={`pdf-upload-zone mx-4 mt-4 mb-2 p-4 border-2 border-dashed rounded-lg transition-all cursor-pointer ${
                  isDraggingPdf ? 'border-orange-500 bg-orange-50' : 'border-zinc-300 hover:border-orange-400 hover:bg-zinc-50'
                } ${isUploadingPdf ? 'opacity-60 pointer-events-none' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !isUploadingPdf && pdfInputRef.current?.click()}
                data-testid="pdf-drop-zone"
              >
                <input ref={pdfInputRef} type="file" accept=".pdf,.eml,.msg" onChange={handleFileInputChange} className="hidden" data-testid="pdf-file-input" />
                <div className="flex items-center justify-center gap-3 text-sm">
                  {isUploadingPdf ? (
                    <>
                      <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                      <span className="text-orange-600 font-medium">Extraction du texte en cours...</span>
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 text-zinc-400" />
                      <span className="text-zinc-500">
                        <span className="font-medium text-zinc-700">Glissez un PDF ou un email (.eml / .msg) ici</span>
                        {" "}ou{" "}
                        <span className="text-orange-500 underline">cliquez pour sélectionner</span>
                      </span>
                      <Upload className="w-4 h-4 text-zinc-400" />
                    </>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 px-4 mb-2">
                <div className="flex-1 h-px bg-zinc-200"></div>
                <span className="text-xs text-zinc-400 uppercase tracking-wider">ou collez votre email</span>
                <div className="flex-1 h-px bg-zinc-200"></div>
              </div>

              {emailCandidates && (emailCandidates.items.length > 0 || emailCandidates.unavailable > 0) && (
                <div className="mx-4 mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-800" data-testid="email-images-indicator">
                  <Palette className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  {emailCandidates.items.length > 0
                    ? <span>{emailCandidates.items.filter(i => i.key).length} image(s) de toile détectée(s) — vous pourrez les vérifier après l'enregistrement de la commande.</span>
                    : <span>Les images n'ont pas pu être copiées depuis la messagerie : glissez le fichier .msg / .eml pour les récupérer.</span>}
                </div>
              )}
              <Textarea
                value={emailContent}
                onPaste={handleEmailPaste}
                onChange={(e) => setEmailContent(e.target.value)}
                placeholder={`Collez le contenu de votre email de commande ici...

Exemple:
De: client@example.com
Objet: Commande n°12345

Bonjour,

Nous souhaitons commander:
- 50 unités du produit A à 25€
- 100 unités du produit B à 15€

Livraison souhaitée: 15 février 2026
Adresse: 123 Rue Example, 75001 Paris

Cordialement,
Jean Dupont
Acme Corp`}
                className="email-textarea border-0 focus:ring-0 bg-zinc-50"
                data-testid="email-input"
              />
            </div>
          </div>
        </div>
      </main>

      {/* Extraction Validation Modal */}
      <ExtractionValidationModal
        isOpen={validationModal.open}
        onClose={() => setValidationModal({ open: false, mode: "extraction", extractedData: {}, existingData: {}, emailContent: "" })}
        mode={validationModal.mode}
        extractedData={validationModal.extractedData}
        existingData={validationModal.existingData}
        potentialDuplicates={validationModal.potentialDuplicates || []}
        invoiceTotals={validationModal.invoiceTotals || null}
        orderId={currentOrderId}
        onSave={handleValidationSave}
        isLoading={isSavingValidation}
      />

      {fabricReview && (
        <FabricReviewModal open orderId={fabricReview.orderId} orderName={fabricReview.orderName}
          candidates={fabricReview.candidates} onClose={() => setFabricReview(null)} />
      )}

      {/* Enrich Modal */}
      <EnrichModal
        isEnrichModalOpen={isEnrichModalOpen} setIsEnrichModalOpen={setIsEnrichModalOpen}
        enrichEmailContent={enrichEmailContent} setEnrichEmailContent={setEnrichEmailContent}
        isEnriching={isEnriching} handleEnrich={handleEnrich}
      />

      {/* Conflicts Modal */}
      <ConflictsModal
        conflictsModal={conflictsModal} setConflictsModal={setConflictsModal}
        customConflictValue={customConflictValue} setCustomConflictValue={setCustomConflictValue}
        resolveConflict={resolveConflict} applyResolvedConflicts={applyResolvedConflicts}
      />
    </div>
  );
}
